import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { PlayerDeVoz } from "./Anexo.jsx";
import { Secao } from "./comuns.jsx";
import { ICONES, Icone, mmss } from "../visual.jsx";

/**
 * A transcrição da reunião amarrada ao áudio.
 *
 * Uma reunião de cinquenta minutos com o texto de um lado e o player do outro
 * não serve para o que o escritório faz com ela: conferir o que o cliente disse
 * sobre o valor, a data, a função. Quem conferia lia o `mm:ss` na tela, arrastava
 * a agulha do player e errava por dez segundos — três vezes, até desistir.
 *
 * Aqui as duas coisas são uma:
 *
 *   - **clicar na fala pula o áudio** para o segundo dela e toca;
 *   - **a fala do momento acende** enquanto o áudio corre (barra verde à
 *     esquerda, fundo suave, tempo em destaque);
 *   - **a lista acompanha sozinha**, e para de acompanhar no instante em que a
 *     pessoa rola com a mão — seguir a agulha por cima de quem está lendo outro
 *     trecho é o pior comportamento possível numa lista longa.
 *
 * O tempo vem do TEXTO da transcrição (`**Nome** (mm:ss): fala`), que é o formato
 * que a diarização por trilha produz em `src/transcricao.ts`. Não há segundo
 * exato por palavra aqui de propósito: o que existe no banco é o minuto da fala,
 * e prometer precisão de palavra que o dado não tem seria inventar.
 */

/** `mm:ss` ou `hh:mm:ss` → segundos. Reunião de duas horas usa o segundo formato. */
export function paraSegundos(marca) {
  const partes = String(marca ?? "")
    .split(":")
    .map((n) => Number(n));
  if (partes.some((n) => !Number.isFinite(n))) return null;
  if (partes.length === 3) return partes[0] * 3600 + partes[1] * 60 + partes[2];
  if (partes.length === 2) return partes[0] * 60 + partes[1];
  return null;
}

/**
 * O texto da transcrição virando falas com tempo.
 *
 * Linha sem marca de tempo não é descartada: transcrição colada à mão (o caminho
 * que sempre existiu) não tem minutagem, e perder o texto por isso seria trocar
 * uma tela pobre por uma tela vazia.
 */
export function separarFalas(transcricao) {
  return String(transcricao ?? "")
    .split("\n")
    .filter((l) => l.trim())
    .map((linha, i) => {
      const m = /^\*\*(.+?)\*\*\s*(?:\((\d{1,2}:\d{2}(?::\d{2})?)\))?:?\s*(.*)$/.exec(linha);
      const marca = m?.[2] ?? "";
      return {
        chave: i,
        quem: m?.[1] ?? "",
        marca,
        segundo: marca ? paraSegundos(marca) : null,
        texto: m ? m[3] : linha,
      };
    });
}

/* Lista vazia estável: literal no parâmetro nasce novo a cada render. */
const SEM_MARCADORES = [];

/** Uma cor por falante, estável: a mesma pessoa tem a mesma cor em toda reunião. */
const CORES = [
  "var(--color-verde-fundo)",
  "var(--color-terra-escura)",
  "var(--color-azul)",
  "var(--color-ambar-escuro)",
  "var(--color-roxo)",
];
const corDe = (nome) => {
  let h = 0;
  for (const ch of String(nome)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CORES[h % CORES.length];
};

export default function TranscricaoTocavel({ transcricao, audio, duracao = 0, marcadores = SEM_MARCADORES, altura = "44vh" }) {
  const falas = useMemo(() => separarFalas(transcricao), [transcricao]);
  const [agora, setAgora] = useState(0);
  const [pedido, setPedido] = useState(null);
  /* Acompanhar a agulha é conveniência, não regra: quem rolou com a mão está
     lendo outro trecho, e puxar a lista de volta seria tirar o texto da frente
     dela. Volta a acompanhar quando a pessoa clica numa fala. */
  const [acompanhar, setAcompanhar] = useState(true);
  /* A dica de "clique numa fala" some depois do primeiro clique: instrução que a
     pessoa já seguiu vira ruído permanente no topo de toda reunião. */
  const [jaClicou, setJaClicou] = useState(false);
  const caixa = useRef(null);
  const ativa = useRef(null);

  const comTempo = falas.some((f) => f.segundo != null);

  /* Qual fala está no ar: a última que começou antes do tempo atual. */
  const indiceAtivo = useMemo(() => {
    if (!comTempo) return -1;
    let achado = -1;
    for (const [i, f] of falas.entries()) if (f.segundo != null && f.segundo <= agora + 0.35) achado = i;
    return achado;
  }, [falas, agora, comTempo]);

  useEffect(() => {
    if (!acompanhar || indiceAtivo < 0) return;
    /* `nearest` e não `center`: com `center` a lista dançava a cada fala curta. */
    ativa.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [indiceAtivo, acompanhar]);

  const irPara = useCallback((segundo) => {
    setAcompanhar(true);
    setJaClicou(true);
    /* `n` faz do segundo clique na MESMA fala um pedido novo. */
    setPedido({ segundo, n: Date.now() });
  }, []);

  return (
    <>
      {audio ? (
        <div className="mb-3">
          <PlayerDeVoz anexo={audio} aoTempo={setAgora} irPara={pedido} duracao={duracao} imediato />
        </div>
      ) : null}

      {marcadores.length ? (
        <>
          <Secao espaco="0 0 6px">MOMENTOS MARCADOS</Secao>
          <div className="mb-3.5 flex flex-wrap gap-1.5">
            {marcadores.map((m) => (
              /* Pastilha que é botão (`render`): tem a cara de etiqueta âmbar e o
                 clique pula o áudio para aquele momento. Sem áudio guardado o
                 clique não faz nada — e a dica é que diz por quê. */
              <Badge
                key={m.id ?? `${m.segundo}-${m.rotulo}`}
                render={<button type="button" />}
                onClick={() => audio && irPara(m.segundo)}
                data-dica={audio ? undefined : "Sem áudio guardado"}
                /* `whitespace-normal` e teto de largura: o selo do registro vem
                   com `nowrap`, e um rótulo comprido ("Pedidos fechados: moral,
                   estético, pensão, estabilidade") saía pela direita da caixa,
                   cortado sem barra de rolagem. */
                className={`h-auto max-w-full gap-[5px] rounded-lg border-ambar/28 bg-ambar/16 px-[9px] py-1 text-left text-apoio leading-[1.4] font-semibold whitespace-normal text-ambar-escuro ${
                  audio ? "cursor-pointer" : "cursor-default"
                }`}
              >
                {audio ? <Icone de={ICONES.tocar} tamanho={11} /> : null}
                {mmss(m.segundo)}
                {m.rotulo ? ` · ${m.rotulo}` : ""}
              </Badge>
            ))}
          </div>
        </>
      ) : null}

      {/* Rótulo e ação na mesma linha, a dica na de baixo.
          Os três brigavam por uma linha só: no painel do atendimento (~330px) a
          frase da dica era comprimida a uma coluna de quatro caracteres, uma
          palavra por linha, e empurrava o botão para fora do alcance da vista. */}
      <div className="mb-1 flex items-center gap-2">
        <Secao espaco="0">TRANSCRIÇÃO</Secao>
        {audio && comTempo && !acompanhar ? (
          <Button
            variant="outline"
            onClick={() => setAcompanhar(true)}
            className="ml-auto h-auto flex-none rounded-lg border-verde-fundo/30 bg-verde/9 px-2 py-[3px] text-selo font-bold text-verde-fundo"
          >
            Acompanhar o áudio
          </Button>
        ) : null}
      </div>
      {audio && comTempo && !jaClicou ? (
        <div className="mb-1.5 text-apoio leading-[1.4] text-muted-foreground">Clique numa fala para ouvir esse trecho.</div>
      ) : null}

      <div
        ref={caixa}
        /* Rolar com a mão desliga o acompanhamento — e `onWheel`/`onTouchMove` em
           vez de `onScroll` porque o próprio `scrollIntoView` dispara `scroll`, o
           que desligaria o acompanhamento no primeiro salto automático.

           E este contêiner NÃO virou `ScrollArea`: é ele que rola, e é dentro
           dele que o `scrollIntoView` da fala no ar calcula o "nearest". */
        onWheel={() => setAcompanhar(false)}
        onTouchMove={() => setAcompanhar(false)}
        className="overflow-y-auto pr-1"
        style={{ maxHeight: altura }}
      >
        {falas.length ? (
          falas.map((f, i) => {
            const noAr = i === indiceAtivo;
            const clicavel = Boolean(audio) && f.segundo != null;
            return (
              <div
                key={f.chave}
                ref={noAr ? ativa : null}
                onClick={clicavel ? () => irPara(f.segundo) : undefined}
                className={`mb-[3px] flex gap-2.5 rounded-[10px] py-[7px] pr-2 transition-colors duration-[var(--t-curta)] ease-entra ${
                  noAr ? "border-l-[3px] border-l-verde bg-verde/8 pl-[7px]" : "pl-[9px]"
                } ${clicavel ? "cursor-pointer" : "cursor-default"}`}
              >
                {f.marca ? (
                  <Badge
                    className={`h-fit flex-none rounded-md px-1.5 py-[3px] font-mono text-selo ${
                      noAr ? "bg-verde/14 font-bold text-verde-fundo" : "bg-foreground/5 font-normal text-muted-foreground"
                    }`}
                  >
                    {f.marca}
                  </Badge>
                ) : null}
                <div className="min-w-0">
                  {f.quem ? (
                    <div className="text-selo font-bold" style={{ color: corDe(f.quem) }}>
                      {f.quem}
                    </div>
                  ) : null}
                  <div className="text-corpo leading-[1.6] text-tinta-media">{f.texto}</div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-corpo text-muted-foreground">Esta gravação não tem transcrição.</div>
        )}
      </div>
    </>
  );
}
